import React, { useEffect, useRef, useMemo } from "react";
import "./KubeIntro.css";
import { useTranslation, Trans } from "react-i18next";

/* Icônes react-unicons (qualité SVG) */
import {
  UilWifi,
  UilLayerGroup,
  UilPhone,
  UilCheckCircle,
  UilWrench,
  UilPlug,
  UilServer,
  UilShieldCheck,
  UilFileAlt,
  UilEnvelope
} from "@iconscout/react-unicons";

/* ============================ Utilitaires géométriques ============================ */
function centerOf(rect){return{ x:rect.left+rect.width/2, y:rect.top+rect.height/2 };}

function intersectSegmentWithRect(p0,p1,rect){
  const x0=p0.x,y0=p0.y,dx=p1.x-x0,dy=p1.y-y0,left=rect.left,right=rect.left+rect.width,top=rect.top,bottom=rect.top+rect.height;
  let t0=0,t1=1;
  const clip=(p,q)=>{ if(p===0) return q<0?false:true; const r=q/p; if(p<0){ if(r>t1)return false; if(r>t0)t0=r; } else { if(r<t0)return false; if(r<t1)t1=r; } return true; };
  if(clip(-dx,x0-left)&&clip(dx,right-x0)&&clip(-dy,y0-top)&&clip(dy,bottom-y0)){ const tEdge=t0>0?t0:(t1>0?t1:0); return{ x:x0+tEdge*dx, y:y0+tEdge*dy }; }
  return { x:x0, y:y0 };
}
function intersectWithRoundedRect(p0,p1,rect,rx=0,ry=0){
  if(!rx&&!ry) return intersectSegmentWithRect(p0,p1,rect);
  rx=Math.max(0,rx); ry=Math.max(0,ry);
  const left=rect.left,right=rect.left+rect.width,top=rect.top,bottom=rect.top+rect.height;
  const q=intersectSegmentWithRect(p0,p1,rect);
  const eps=0.5,onLeft=Math.abs(q.x-left)<=eps,onRight=Math.abs(q.x-right)<=eps,onTop=Math.abs(q.y-top)<=eps,onBottom=Math.abs(q.y-bottom)<=eps;
  const inTopSpan=q.x>=left+rx-eps&&q.x<=right-rx+eps,inLeftSpan=q.y>=top+ry-eps&&q.y<=bottom-ry+eps;
  if((onTop&&inTopSpan)||(onBottom&&inTopSpan)||(onLeft&&inLeftSpan)||(onRight&&inLeftSpan)) return q;
  let cx,cy;
  if(onTop&&q.x<left+rx){cx=left+rx;cy=top+ry;} else if(onTop&&q.x>right-rx){cx=right-rx;cy=top+ry;}
  else if(onBottom&&q.x>right-rx){cx=right-rx;cy=bottom-ry;} else if(onBottom&&q.x<left+rx){cx=left+rx;cy=bottom-ry;}
  else if(onLeft&&q.y<top+ry){cx=left+rx;cy=top+ry;} else if(onLeft&&q.y>bottom-ry){cx=left+rx;cy=bottom-ry;}
  else if(onRight&&q.y<top+ry){cx=right-rx;cy=top+ry;} else if(onRight&&q.y>bottom-ry){cx=right-rx;cy=bottom-ry;}
  else return q;
  const dx=p1.x-p0.x,dy=p1.y-p0.y,X0=p0.x-cx,Y0=p0.y-cy;
  const A=(dx*dx)/(rx*rx)+(dy*dy)/(ry*ry),B=2*((X0*dx)/(rx*rx)+(Y0*dy)/(ry*ry)),C=(X0*X0)/(rx*rx)+(Y0*Y0)/(ry*ry)-1,disc=B*B-4*A*C;
  if(disc<0||A===0) return q;
  const sqrtDisc=Math.sqrt(disc),t1=(-B-sqrtDisc)/(2*A),t2=(-B+sqrtDisc)/(2*A);
  let t=Number.POSITIVE_INFINITY; if(t1>0&&t1<t)t=t1; if(t2>0&&t2<t)t=t2; if(!isFinite(t)) return q;
  return { x:p0.x+t*dx, y:p0.y+t*dy };
}
function getBorderRadiusPx(el,fallback=0){ if(!el) return fallback; const cs=getComputedStyle(el); const raw=cs.borderTopLeftRadius||cs.borderRadius||""; const num=parseFloat(raw); return Number.isFinite(num)?num:fallback; }
function computeAnchorsRounded(hubTargetRect,hubRx,hubRy,nodeRect,nodeRx,nodeRy,rootRect){
  const hubC=centerOf(hubTargetRect), nodeC=centerOf(nodeRect);
  const hubEdge=intersectWithRoundedRect(hubC,nodeC,{left:hubTargetRect.left,top:hubTargetRect.top,width:hubTargetRect.width,height:hubTargetRect.height},hubRx,hubRy);
  const nodeEdge=intersectWithRoundedRect(nodeC,hubC,{left:nodeRect.left,top:nodeRect.top,width:nodeRect.width,height:nodeRect.height},nodeRx,nodeRy);
  return { start:{ x:hubEdge.x-rootRect.left, y:hubEdge.y-rootRect.top }, end:{ x:nodeEdge.x-rootRect.left, y:nodeEdge.y-rootRect.top } };
}

export default function KubeIntro() {
  const { t, i18n } = useTranslation();

  // ===== Gestion du manuel en fonction de la langue (FR/EN) =====
  const lang = (i18n?.resolvedLanguage || i18n?.language || "fr").toLowerCase();
  const isFr = lang.startsWith("fr");

  // URL safe (espaces) => encodeURIComponent
  const manualPath = useMemo(() => {
    const fileName = isFr ? "KUBE ISS_FR.pdf" : "ISS KUBE_EN.pdf";
    return `/docs/${encodeURIComponent(fileName)}`;
  }, [isFr]);

  const manualDownloadName = useMemo(() => {
    // Nom proposé dans "Enregistrer sous..." (optionnel mais propre)
    return isFr ? "KUBE_ISS_FR.pdf" : "ISS_KUBE_EN.pdf";
  }, [isFr]);

  // ✅ Image schéma selon la langue (FR / EN)
  const schemaImgSrc = useMemo(() => {
    return isFr ? "/images/Schema_kube.png" : "/images/Schema_kube_en.png";
  }, [isFr]);

  const productLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": t("kubeIntro.product_name"),
    "brand": { "@type": "Organization", "name": t("kubeIntro.jsonld_brand") },
    "category": t("kubeIntro.jsonld_category"),
    "description": t("kubeIntro.jsonld_description"),
    "keywords": t("kubeIntro.jsonld_keywords"),
    "manufacturer": { "@type": "Organization", "name": t("kubeIntro.jsonld_manu") },
    "model": t("kubeIntro.jsonld_model"),
    "additionalProperty": [
      { "@type": "PropertyValue", "name": t("kubeIntro.prop_serial_name"), "value": t("kubeIntro.prop_serial_val") },
      { "@type": "PropertyValue", "name": t("kubeIntro.prop_autopin_name"), "value": t("kubeIntro.prop_autopin_val") },
      { "@type": "PropertyValue", "name": t("kubeIntro.prop_updates_name"), "value": t("kubeIntro.prop_updates_val") },
      { "@type": "PropertyValue", "name": t("kubeIntro.prop_conn_name"), "value": t("kubeIntro.prop_conn_val") },
      { "@type": "PropertyValue", "name": t("kubeIntro.prop_bus_name"), "value": t("kubeIntro.prop_bus_val") },
      { "@type": "PropertyValue", "name": t("kubeIntro.prop_power_name"), "value": t("kubeIntro.prop_power_val") },
      { "@type": "PropertyValue", "name": t("kubeIntro.prop_superv_name"), "value": t("kubeIntro.prop_superv_val") }
    ]
  };

  // Réfs
  const diagramRef = useRef(null);
  const hubRef = useRef(null);
  const hubImgRef = useRef(null);
  const svgRef = useRef(null);

  // Dessin des liaisons
  useEffect(() => {
    const root=diagramRef.current, hub=hubRef.current, hubImg=hubImgRef.current, svg=svgRef.current;
    if(!root||!hub||!hubImg||!svg) return;
    const draw=()=> {
      const rect=root.getBoundingClientRect();
      svg.setAttribute("width",`${rect.width}`);
      svg.setAttribute("height",`${rect.height}`);
      svg.setAttribute("viewBox",`0 0 ${rect.width} ${rect.height}`);
      const hubRect=hubImg.getBoundingClientRect(), hubR=getBorderRadiusPx(hubImg,16), hubRx=hubR, hubRy=hubR;
      while(svg.lastChild && svg.lastChild.nodeName!=="defs"){ svg.removeChild(svg.lastChild); }
      const nodes=Array.from(root.querySelectorAll(".node"));
      nodes.forEach(node=>{
        const nodeRect=node.getBoundingClientRect(), nodeR=getBorderRadiusPx(node,14);
        const { start, end }=computeAnchorsRounded(hubRect,hubRx,hubRy,nodeRect,nodeR,nodeR,rect);
        const dx=end.x-start.x, dy=end.y-start.y, len=Math.hypot(dx,dy)||1, mx=(start.x+end.x)/2, my=(start.y+end.y)/2, perp=0.06*len, ux=dx/len, uy=dy/len, cx=mx-uy*perp, cy=my+ux*perp;
        const path=document.createElementNS("http://www.w3.org/2000/svg","path");
        path.setAttribute("d",`M ${start.x},${start.y} Q ${cx},${cy} ${end.x},${end.y}`);
        path.setAttribute("class","linker-path");
        path.setAttribute("stroke","url(#linkerGradient)");
        path.setAttribute("filter","url(#linkerGlow)");
        svg.appendChild(path);
        const dot=document.createElementNS("http://www.w3.org/2000/svg","circle");
        dot.setAttribute("cx",`${end.x}`); dot.setAttribute("cy",`${end.y}`); dot.setAttribute("r","4"); dot.setAttribute("class","linker-dot");
        svg.appendChild(dot);
      });
    };
    const rafDraw=()=>requestAnimationFrame(draw); rafDraw();
    const ro=new ResizeObserver(rafDraw); ro.observe(root); ro.observe(hub); ro.observe(hubImg);
    const mo=new MutationObserver(rafDraw); mo.observe(root,{attributes:true,childList:true,subtree:true});
    window.addEventListener("resize",rafDraw);
    return ()=>{ window.removeEventListener("resize",rafDraw); ro.disconnect(); mo.disconnect(); };
  }, []);

  return (
    <section className="product-intro" aria-labelledby="intro-title" role="region">
      <div className="product-intro-outer">
        <article className="product-intro-card" itemScope itemType="https://schema.org/Product">
          <header className="product-intro-head">
            <h2 id="intro-title" className="product-intro-title" itemProp="name">
              {t("kubeIntro.product_name")}
            </h2>

            <p className="product-intro-deck" itemProp="description">
              <Trans i18nKey="kubeIntro.deck_html" components={{ strong: <strong /> }} />
            </p>
          </header>

          <div className="product-intro-body">
            {/* ---------- POINTS FORTS ---------- */}
            <h3 className="product-intro-sub">{t("kubeIntro.whyTitle")}</h3>
            <ul className="features-rail" aria-label={t("kubeIntro.features_aria")}>
              <li className="feature" tabIndex={0}>
                <div className="feature-badge" aria-hidden="true"><UilLayerGroup /></div>
                <h4 className="feature-title">{t("kubeIntro.f1_title")}</h4>
                <p className="feature-text">{t("kubeIntro.f1_text")}</p>
              </li>
              <li className="feature" tabIndex={0}>
                <div className="feature-badge" aria-hidden="true"><UilPlug /></div>
                <h4 className="feature-title">{t("kubeIntro.f2_title")}</h4>
                <p className="feature-text">{t("kubeIntro.f2_text")}</p>
              </li>
              <li className="feature" tabIndex={0}>
                <div className="feature-badge" aria-hidden="true"><UilServer /></div>
                <h4 className="feature-title">{t("kubeIntro.f3_title")}</h4>
                <p className="feature-text">{t("kubeIntro.f3_text")}</p>
              </li>
              <li className="feature" tabIndex={0}>
                <div className="feature-badge" aria-hidden="true"><UilWrench /></div>
                <h4 className="feature-title">{t("kubeIntro.f4_title")}</h4>
                <p className="feature-text">{t("kubeIntro.f4_text")}</p>
              </li>
              <li className="feature" tabIndex={0}>
                <div className="feature-badge" aria-hidden="true"><UilShieldCheck /></div>
                <h4 className="feature-title">{t("kubeIntro.f5_title")}</h4>
                <p className="feature-text">{t("kubeIntro.f5_text")}</p>
              </li>
            </ul>

            {/* ---------- DIAGRAMME ---------- */}
            <h3 className="product-intro-sub">{t("kubeIntro.diffTitle")}</h3>
            <div className="diff-diagram" aria-label={t("kubeIntro.hub_aria")} ref={diagramRef}>
              <svg className="diff-linker" ref={svgRef} aria-hidden="true">
                <defs>
                  <linearGradient id="linkerGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="rgba(0,112,173,0.45)" />
                    <stop offset="100%" stopColor="rgba(59,167,225,0.95)" />
                  </linearGradient>
                  <filter id="linkerGlow" x="-20%" y="-20%" width="140%" height="140%">
                    <feDropShadow dx="0" dy="0" stdDeviation="2" floodColor="rgba(0,112,173,0.35)" />
                  </filter>
                </defs>
              </svg>

              <div className="hub" aria-label={t("kubeIntro.hub_aria")} ref={hubRef}>
                <img
                  ref={hubImgRef}
                  src={"/images/Kube_part.png"}
                  alt={t("kubeIntro.hub_img_alt")}
                  className="hub-img"
                  loading="lazy"
                />
                <span className="sr-only">{t("kubeIntro.hub_sr")}</span>
              </div>

              <div className="rays" aria-hidden="true">
                <span className="ray r1"></span><span className="ray r2"></span>
                <span className="ray r3"></span><span className="ray r4"></span>
                <span className="ray r5"></span><span className="ray r6"></span>
                <span className="ray r7"></span><span className="ray r8"></span>
              </div>

              <ul className="nodes">
                <li className="node n1" tabIndex={0}>
                  <div className="node-icon"><UilLayerGroup /></div>
                  <h4 className="node-title">{t("kubeIntro.n1_title")}</h4>
                  <p className="node-text">{t("kubeIntro.n1_text")}</p>
                </li>
                <li className="node n2" tabIndex={0}>
                  <div className="node-icon"><UilPlug /></div>
                  <h4 className="node-title">{t("kubeIntro.n2_title")}</h4>
                  <p className="node-text">{t("kubeIntro.n2_text")}</p>
                </li>
                <li className="node n3" tabIndex={0}>
                  <div className="node-icon"><UilServer /></div>
                  <h4 className="node-title">{t("kubeIntro.n3_title")}</h4>
                  <p className="node-text">{t("kubeIntro.n3_text")}</p>
                </li>
                <li className="node n4" tabIndex={0}>
                  <div className="node-icon"><UilCheckCircle /></div>
                  <h4 className="node-title">{t("kubeIntro.n4_title")}</h4>
                  <p className="node-text">{t("kubeIntro.n4_text")}</p>
                </li>
                <li className="node n5" tabIndex={0}>
                  <div className="node-icon"><UilWifi /></div>
                  <h4 className="node-title">{t("kubeIntro.n5_title")}</h4>
                  <p className="node-text">{t("kubeIntro.n5_text")}</p>
                </li>
                <li className="node n6" tabIndex={0}>
                  <div className="node-icon"><UilWrench /></div>
                  <h4 className="node-title">{t("kubeIntro.n6_title")}</h4>
                  <p className="node-text">{t("kubeIntro.n6_text")}</p>
                </li>
                <li className="node n7" tabIndex={0}>
                  <div className="node-icon"><UilShieldCheck /></div>
                  <h4 className="node-title">{t("kubeIntro.n7_title")}</h4>
                  <p className="node-text">{t("kubeIntro.n7_text")}</p>
                </li>
                <li className="node n8" tabIndex={0}>
                  <div className="node-icon"><UilPhone /></div>
                  <h4 className="node-title">{t("kubeIntro.n8_title")}</h4>
                  <p className="node-text">{t("kubeIntro.n8_text")}</p>
                </li>
              </ul>
            </div>

            {/* ---------- How-to ---------- */}
            <h3 className="product-intro-sub">{t("kubeIntro.howTitle")}</h3>
            <div className="product-howto" aria-labelledby="howto-caption">
              <div className="howto-steps" role="list">
                <ol className="howto-list" aria-label={t("kubeIntro.how_aria")}>
                  <li><Trans i18nKey="kubeIntro.s1_html" components={{ strong: <strong /> }} /></li>
                  <li><Trans i18nKey="kubeIntro.s2_html" components={{ strong: <strong />, em: <em /> }} /></li>
                  <li><Trans i18nKey="kubeIntro.s3_html" components={{ strong: <strong /> }} /></li>
                </ol>
                <p className="howto-hint"><Trans i18nKey="kubeIntro.howto_hint_html" components={{ strong: <strong /> }} /></p>
              </div>

              <figure className="howto-figure">
                <img
                  src={schemaImgSrc}
                  alt={t("kubeIntro.fig_img_alt")}
                  className="howto-image"
                />
                <figcaption id="howto-caption" className="howto-caption">
                  <Trans i18nKey="kubeIntro.fig_caption_html" components={{ strong: <strong /> }} />
                </figcaption>
              </figure>
            </div>

            {/* ---------- Spécifications ---------- */}
            <h3 className="product-intro-sub">{t("kubeIntro.specsTitle")}</h3>
            <div className="table-wrap">
              <table className="specs" aria-describedby="specs-caption">
                <caption id="specs-caption" className="specs-caption">
                  {t("kubeIntro.specs_caption")}
                </caption>
                <tbody>
                  <tr>
                    <th scope="row"><Trans i18nKey="kubeIntro.th_box_fix" /></th>
                    <td>
                      <ul>
                        <li><Trans i18nKey="kubeIntro.li_box_1_html" components={{ strong: <strong /> }} /></li>
                      </ul>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">{t("kubeIntro.th_power")}</th>
                    <td>
                      <ul>
                        <li><Trans i18nKey="kubeIntro.li_power_1_html" components={{ strong: <strong /> }} /></li>
                        <li><Trans i18nKey="kubeIntro.li_power_2_html" components={{ strong: <strong /> }} /></li>
                        <li><Trans i18nKey="kubeIntro.li_power_3_html" components={{ strong: <strong /> }} /></li>
                      </ul>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">{t("kubeIntro.th_cabinet")}</th>
                    <td>
                      <ul>
                        <li><Trans i18nKey="kubeIntro.li_cab_1_html" components={{ strong: <strong /> }} /></li>
                        <li><Trans i18nKey="kubeIntro.li_cab_2_html" components={{ strong: <strong /> }} /></li>
                      </ul>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row"><Trans i18nKey="kubeIntro.th_proto" /></th>
                    <td>
                      <ul>
                        <li><Trans i18nKey="kubeIntro.li_proto_1_html" components={{ strong: <strong /> }} /></li>
                        <li><Trans i18nKey="kubeIntro.li_proto_2_html" components={{ strong: <strong /> }} /></li>
                      </ul>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">{t("kubeIntro.th_maint")}</th>
                    <td>
                      <ul>
                        <li><Trans i18nKey="kubeIntro.li_maint_1_html" components={{ strong: <strong /> }} /></li>
                        <li>{t("kubeIntro.li_maint_2")}</li>
                      </ul>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">{t("kubeIntro.th_platform")}</th>
                    <td>
                      <ul>
                        <li><Trans i18nKey="kubeIntro.li_platform_1_html" components={{ strong: <strong /> }} /></li>
                      </ul>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* ---------- CTA fin de page ---------- */}
            <div className="product-ctas" role="group" aria-label={t("kubeIntro.ctas_aria")}>
              <a
                className="btn btn-primary"
                href={manualPath}
                download={manualDownloadName}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="btn-ico" aria-hidden="true"><UilFileAlt size="20" /></span>
                <span>{t("kubeIntro.cta_manual")}</span>
              </a>

              <a className="btn btn-outline" href="/devis">
                <span className="btn-ico" aria-hidden="true"><UilEnvelope size="20" /></span>
                <span>{t("kubeIntro.cta_quote")}</span>
              </a>
            </div>

            {/* JSON-LD SEO */}
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(productLd) }}
            />
          </div>
        </article>
      </div>
    </section>
  );
}
